import { type ReactNode, useCallback, useEffect, useState } from "react";
import { api } from "@/lib/http.ts";
import { pushSubscriptionQueries } from "@/components/notifications/pushSubscriptionQueries.ts";
import * as Sentry from "@sentry/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { NotificationContext } from "./NotificationContext.tsx";

const urlBase64ToUint8Array = (base64String: string): Uint8Array<ArrayBuffer> => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const buffer = new ArrayBuffer(rawData.length);
  const outputArray = new Uint8Array(buffer);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

const registerServiceWorker = async () => {
  if ("serviceWorker" in navigator) {
    const registration = await navigator.serviceWorker.register("/sw.js", {
      scope: "/",
    });
    console.log("Service Worker Registered...", registration);
    return registration;
  }
};

const subscribeToPushNotifications = async (registration: ServiceWorkerRegistration) => {
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(import.meta.env["VITE_VAPID_PUBLIC_KEY"] || ""),
  });
  console.log(
    "Successfully subscribed to push notifications",
    JSON.stringify(subscription, null, 2),
  );
  return subscription.toJSON();
};

const detectPlatform = (): string => {
  const ua = navigator.userAgent.toLowerCase();

  if (ua.includes("chrome") && !ua.includes("edge") && !ua.includes("opr")) {
    return "chrome";
  }
  if (ua.includes("firefox")) {
    return "firefox";
  }
  if (ua.includes("safari") && !ua.includes("chrome")) {
    return "safari";
  }
  if (ua.includes("edge") || ua.includes("edg")) {
    return "edge";
  }
  if (ua.includes("opr") || ua.includes("opera")) {
    return "opera";
  }

  return "unknown";
};

/**
 * Subscribe this browser to push if it isn't already, and register the
 * signed-in account on that subscription. Subscribing is idempotent: a browser
 * that already has a subscription hands the same one back.
 *
 * Returns the browser's push endpoint, which keys the account's status.
 */
const subscribeAccount = async (): Promise<string> => {
  const registration = await registerServiceWorker();
  if (!registration) throw new Error("Service workers are not supported");
  const subscription = await subscribeToPushNotifications(registration);
  if (!subscription.endpoint) throw new Error("Push subscription has no endpoint");
  await api.post("/api/a/subscriptions/new", { ...subscription, platform: detectPlatform() });
  return subscription.endpoint;
};

/**
 * This browser's current push endpoint, or null when it has no subscription.
 *
 * `getRegistration`, not `ready`: `ready` never settles in a browser where the
 * service worker was never registered.
 */
const getBrowserEndpoint = async (): Promise<string | null> => {
  if (!("serviceWorker" in navigator)) return null;
  const registration = await navigator.serviceWorker.getRegistration("/");
  const subscription = await registration?.pushManager.getSubscription();
  return subscription?.endpoint ?? null;
};

/**
 * Turn notifications off for the signed-in account on this browser.
 *
 * The browser's own subscription is left in place: every account signed in on
 * this browser shares it, so unsubscribing here would silence the others too.
 */
const unsubscribeAccount = async (endpoint: string) => {
  await api.post("/api/a/subscriptions/delete", { endpoint });
};

/**
 * Represents the possible permission states for browser notifications.
 * @property default - Initial state, permission not yet requested
 * @property granted - User has granted permission
 * @property denied - User has denied permission
 */
type NotificationPermission = "default" | "granted" | "denied";

type NotificationProviderProps = {
  children: ReactNode;
};

/**
 * Provider component that manages browser notification permissions and support.
 * Wraps the application to provide notification functionality to child components.
 *
 * Whether the signed-in account receives pushes on this browser is server
 * state, read through TanStack Query and written back by the on/off
 * mutations. Reading it never subscribes anything: an account receives pushes
 * here only once its own switch turned them on.
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <NotificationProvider>
 *       <YourApp />
 *     </NotificationProvider>
 *   );
 * }
 * ```
 */
export function NotificationProvider({ children }: NotificationProviderProps) {
  const queryClient = useQueryClient();
  const [permission, setPermission] = useState<NotificationPermission>("default");
  const [isSupported, setIsSupported] = useState(false);
  // The browser's push endpoint, once permission is granted and it has been
  // read. Null until then, which keeps the status query idle.
  const [endpoint, setEndpoint] = useState<string | null>(null);

  const status = useQuery(pushSubscriptionQueries.status(endpoint));
  const isSubscribed = status.data ?? false;

  useEffect(() => {
    const supported = "Notification" in window;
    setIsSupported(supported);
    if (!supported) return;

    setPermission(Notification.permission);
    if (Notification.permission !== "granted") return;

    getBrowserEndpoint()
      .then(setEndpoint)
      .catch((error) => {
        Sentry.captureException(error);
        console.error("Failed to read the browser's push subscription:", error);
      });
  }, []);

  /**
   * Write the account's answer for `endpoint` straight into the cache, after
   * cancelling any read still in flight: a status read started before the
   * user acted would otherwise land after the write and show the old state.
   */
  const settleStatus = useCallback(
    async (nextEndpoint: string, subscribed: boolean) => {
      await queryClient.cancelQueries({ queryKey: pushSubscriptionQueries.all() });
      queryClient.setQueryData(pushSubscriptionQueries.status(nextEndpoint).queryKey, subscribed);
      setEndpoint(nextEndpoint);
    },
    [queryClient],
  );

  const enable = useMutation({
    mutationFn: subscribeAccount,
    onSuccess: (nextEndpoint) => settleStatus(nextEndpoint, true),
  });

  const disable = useMutation({
    mutationFn: async () => {
      const current = endpoint ?? (await getBrowserEndpoint());
      if (current) await unsubscribeAccount(current);
      return current;
    },
    onSuccess: (current) => (current ? settleStatus(current, false) : undefined),
  });

  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!("Notification" in window)) {
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result !== "granted") return false;

      await enable.mutateAsync();
      return true;
    } catch (error) {
      Sentry.captureException(error);
      console.error("Error requesting notification permission:", error);
      return false;
    }
  }, [enable]);

  const disableNotifications = useCallback(async (): Promise<boolean> => {
    try {
      await disable.mutateAsync();
      return true;
    } catch (error) {
      Sentry.captureException(error);
      console.error("Error unsubscribing from notifications:", error);
      return false;
    }
  }, [disable]);

  return (
    <NotificationContext.Provider
      value={{
        permission,
        requestPermission,
        disableNotifications,
        isSupported,
        isSubscribed,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}
